import { useEffect, useRef } from "react"
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router"
import { motion, AnimatePresence } from "motion/react"

import { ProtectedRoute } from "@/lib/auth/protected-route"
import { useOrganization } from "@/lib/organization/organization-context"
import { useTeam } from "@/lib/api/use-teams"
import { SettingsShell } from "@/components/settings/settings-shell"
import { SettingsSidebar } from "@/components/settings/settings-sidebar"
import { OrgChartPanel } from "@/components/settings/team/org-chart-panel"
import { TeamMembersPanel } from "@/components/settings/team/team-members-panel"
import { AccountSettings } from "@/components/settings/account-settings"
import { GroupSettings } from "@/components/settings/group-settings"
import { StoreSettings } from "@/components/settings/store-settings"
import { FeesSettings } from "@/components/settings/fees-settings"
import { BodyShopsSettings } from "@/components/settings/body-shops-settings"
import { DealershipsSettings } from "@/components/settings/dealerships-settings"
import { ServiceLaneSettings } from "@/components/settings/service-lane-settings"
import { GoalsRewardsSettings } from "@/components/settings/goals-rewards-settings"
import { MarketingSuperchargeSettings } from "@/components/settings/marketing-supercharge-settings"
import { BookingCalendarSettings } from "@/components/settings/booking-calendar-settings"
import { ConsumerIntakeSettings } from "@/components/settings/consumer-intake/consumer-intake-settings"

// Settings page — entity-first navigation inside an onboarding-shaped
// takeover.
//
// URL shape:
//   /settings                                 → smart redirect (last visited or fallback chain)
//   /settings/account                         → personal account (no sidebar)
//   /settings/group/:teamId/:section          section ∈ business-info | members | network
//   /settings/dealership/:teamId/:section     section ∈ business-info | members | service-lane | goals | body-shop
//   /settings/bodyshop/:teamId/:section       section ∈ business-info | members | fees
//                                                       | dealers
//
// Old `?tab=foo` URLs alias for one release. Drop the helper after.
//
// Bidirectional team sync: when the URL kind/teamId changes, OrganizationContext
// follows (so the topbar switcher reflects). Conversely, picking a team in
// the topbar updates context, which this page watches and re-routes to the
// new team's same section. Single source of truth, two surfaces editing it.

type TeamKind = "group" | "dealership" | "bodyshop"

const SECTION_LABELS: Record<string, string> = {
  "business-info": "Business info",
  members: "Members & invites",
  network: "Network",
  "body-shop": "Body shop",
  fees: "Rates & fees",
  intake: "Consumer intake",
  dealers: "Your dealers",
  "service-lane": "Service lane",
  goals: "Goals & rewards",
  marketing: "Marketing Supercharge",
  booking: "Booking calendar",
}

const KIND_LABEL: Record<TeamKind, string> = {
  group: "Group",
  dealership: "Dealership",
  bodyshop: "Body shop",
}

// Sections each team kind actually exposes. Mirrors the per-kind section
// arrays in settings-sidebar.tsx — kept duplicated rather than imported so
// settings.tsx doesn't reach into the sidebar's internals just for this
// validity check. Used by `safeSectionFor` to swap the URL section when
// switching kinds: e.g. /settings/bodyshop/175/rewards → switch to a
// dealership and the new URL falls back to /settings/dealership/X/business-info
// instead of landing on a NotFound for "rewards" on a dealership.
const VALID_SECTIONS_BY_KIND: Record<TeamKind, readonly string[]> = {
  group: ["business-info", "members", "network"],
  dealership: ["business-info", "members", "service-lane", "goals", "body-shop"],
  bodyshop: ["business-info", "members", "fees", "intake", "marketing", "booking", "dealers"],
}

function safeSectionFor(kind: TeamKind, currentSection: string | undefined): string {
  if (currentSection && VALID_SECTIONS_BY_KIND[kind].includes(currentSection)) {
    return currentSection
  }
  // Every kind has business-info; it's the safest landing for cross-kind
  // navigation. (members would also work, but business-info is consistent
  // with the user's mental model of "switching to a different team's
  // overview" rather than "jumping to its members list".)
  return "business-info"
}

// localStorage key for the user's last-visited Settings section.
// Powers the smart redirect on bare /settings — a return visit lands on
// where you were, not on a generic default.
const LAST_VISITED_KEY = "etx_settings_last_section"

interface LastVisited {
  kind: TeamKind | "account"
  teamId?: string
  section?: string
}

function readLastVisited(): LastVisited | null {
  try {
    const raw = localStorage.getItem(LAST_VISITED_KEY)
    if (!raw) return null
    return JSON.parse(raw) as LastVisited
  } catch {
    return null
  }
}

function writeLastVisited(v: LastVisited): void {
  try {
    localStorage.setItem(LAST_VISITED_KEY, JSON.stringify(v))
  } catch {
    // localStorage may be unavailable (private mode) — fail silently.
  }
}

export default function SettingsPage() {
  return (
    <ProtectedRoute>
      <Router />
    </ProtectedRoute>
  )
}

function Router() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const params = useParams()
  const {
    activeGroup,
    activeDealershipId,
    activeBodyShopId,
    activeTeamKind,
    isGroupView,
    setActiveGroup,
    setGroupView,
    setActiveTeam,
  } = useOrganization()

  const path = location.pathname
  const kind = params.kind as TeamKind | undefined
  const teamId = params.teamId
  const section = params.section

  // Legacy ?tab=foo redirect.
  const legacyTab = searchParams.get("tab")
  const orgReady = !!activeGroup || !!activeDealershipId || !!activeBodyShopId
  useEffect(() => {
    if (!legacyTab || !orgReady) return
    const target = legacyTabRedirect(legacyTab, {
      groupId: activeGroup ? String(activeGroup.id) : null,
      dealershipId: activeDealershipId ?? null,
      bodyshopId: activeBodyShopId ?? null,
    })
    if (target) navigate(target, { replace: true })
  }, [legacyTab, orgReady, activeGroup, activeDealershipId, activeBodyShopId, navigate])

  // URL → Context sync.
  //
  // Fires only on a REAL URL change (kind/teamId pair we haven't synced
  // before). On every other render this is a no-op — otherwise it fights
  // the Context → URL effect below: a topbar click flips isGroupView, and
  // before Context → URL can navigate, this effect would "fix" the
  // context to match the still-stale URL — and the two ping-pong forever.
  //
  // The lastSyncedRef gate breaks the symmetry: once we've synced for a
  // given (kind, teamId), context wins until the URL changes again
  // (paste, back button, sidebar nav).
  const lastSyncedRef = useRef<{ kind?: string; teamId?: string }>({})
  useEffect(() => {
    if (!kind || !teamId) return
    const last = lastSyncedRef.current
    if (last.kind === kind && last.teamId === teamId) return
    lastSyncedRef.current = { kind, teamId }

    if (kind === "group") {
      if (!activeGroup || String(activeGroup.id) !== teamId) {
        setActiveGroup(teamId)
      }
      if (!isGroupView) setGroupView(true)
    } else if (kind === "dealership") {
      if (activeDealershipId !== teamId) setActiveTeam("dealership", teamId)
      if (isGroupView) setGroupView(false)
    } else if (kind === "bodyshop") {
      if (activeBodyShopId !== teamId) setActiveTeam("bodyshop", teamId)
      if (isGroupView) setGroupView(false)
    }
  }, [
    kind,
    teamId,
    activeGroup,
    activeDealershipId,
    activeBodyShopId,
    isGroupView,
    setActiveGroup,
    setGroupView,
    setActiveTeam,
  ])

  // Context → URL sync. When the topbar switcher changes the active team,
  // navigate to the same section on the new team so the user stays in
  // their current task. Skipped when on /settings/account (no entity
  // context) and on bare /settings (the redirect block below handles it).
  //
  // Priority is `activeTeamKind`-led: a user with both an active dealership
  // and an active bodyshop must pick the one that matches their most
  // recent topbar action, not whichever happens to be set first in the
  // chain. Falling back to whichever id exists keeps single-team users
  // working.
  useEffect(() => {
    if (!kind || !teamId || !section) return
    if (path === "/settings/account") return
    let ctxKind: TeamKind | null = null
    let ctxId: string | null = null
    if (isGroupView && activeGroup) {
      ctxKind = "group"
      ctxId = String(activeGroup.id)
    } else if (activeTeamKind === "dealership" && activeDealershipId) {
      ctxKind = "dealership"
      ctxId = activeDealershipId
    } else if (activeTeamKind === "bodyshop" && activeBodyShopId) {
      ctxKind = "bodyshop"
      ctxId = activeBodyShopId
    } else if (activeDealershipId) {
      ctxKind = "dealership"
      ctxId = activeDealershipId
    } else if (activeBodyShopId) {
      ctxKind = "bodyshop"
      ctxId = activeBodyShopId
    }
    if (!ctxKind || !ctxId) return
    if (ctxKind !== kind || ctxId !== teamId) {
      // Section may not exist on the new kind (e.g., /bodyshop/.../rewards
      // → switch to a dealership: dealership has no `rewards`). Swap to a
      // section the new kind actually serves so the user doesn't land on
      // NotFound.
      const nextSection = safeSectionFor(ctxKind, section)
      // Update lastSyncedRef pre-emptively so the URL → Context effect
      // doesn't try to "re-sync" the context after we navigate (the URL
      // change we're triggering is the result of context, not the other
      // way around).
      lastSyncedRef.current = { kind: ctxKind, teamId: ctxId }
      navigate(`/settings/${ctxKind}/${ctxId}/${nextSection}`, { replace: true })
    }
  }, [
    isGroupView,
    activeGroup,
    activeDealershipId,
    activeBodyShopId,
    activeTeamKind,
    kind,
    teamId,
    section,
    path,
    navigate,
  ])

  // Persist last-visited so a return visit to bare /settings lands here.
  // Account is intentionally NOT persisted: it has no team sidebar, so
  // pinning it as the redirect target traps users — every later hit on
  // bare /settings would bounce them back to Account with no in-Settings
  // way out. Account stays reachable via the topbar pill regardless.
  useEffect(() => {
    if (kind && teamId && section) {
      writeLastVisited({ kind, teamId, section })
    }
  }, [kind, teamId, section])

  // Bare /settings → smart redirect. Last-visited if it's still valid for
  // the current org; otherwise the fallback chain (dealership → bodyshop
  // → group → account).
  if (path === "/settings" || path === "/settings/") {
    if (legacyTab) {
      return (
        <SettingsShell>
          <p className="text-sm text-zinc-400">Loading…</p>
        </SettingsShell>
      )
    }
    if (!orgReady) {
      return (
        <SettingsShell>
          <p className="text-sm text-zinc-400">Loading…</p>
        </SettingsShell>
      )
    }

    const last = readLastVisited()
    const validLast = lastVisitedTarget(last, {
      groupId: activeGroup ? String(activeGroup.id) : null,
      dealershipId: activeDealershipId ?? null,
      bodyshopId: activeBodyShopId ?? null,
    })
    if (validLast) return <Navigate to={validLast} replace />

    const target = activeDealershipId
      ? `/settings/dealership/${activeDealershipId}/members`
      : activeBodyShopId
        ? `/settings/bodyshop/${activeBodyShopId}/members`
        : activeGroup
          ? `/settings/group/${activeGroup.id}/members`
          : "/settings/account"
    return <Navigate to={target} replace />
  }

  if (path === "/settings/account") {
    return (
      <SettingsShell>
        <AccountContent />
      </SettingsShell>
    )
  }

  if (!kind || !teamId || !section) {
    return <Navigate to="/settings/account" replace />
  }

  return (
    <SettingsShell sidebar={<SettingsSidebar />}>
      <SectionContent kind={kind} teamId={teamId} section={section} />
    </SettingsShell>
  )
}

function AccountContent() {
  return (
    <SectionPage eyebrow="Personal" title="Your account">
      <AccountSettings />
    </SectionPage>
  )
}

function SectionContent({
  kind,
  teamId,
  section,
}: {
  kind: TeamKind
  teamId: string
  section: string
}) {
  const { data: team } = useTeam(teamId)
  const teamName = team?.name ?? ""
  const sectionLabel = SECTION_LABELS[section] ?? section
  const eyebrow = teamName
    ? `${teamName} · ${KIND_LABEL[kind]}`
    : KIND_LABEL[kind]

  return (
    <SectionPage eyebrow={eyebrow} title={sectionLabel}>
      <Panel kind={kind} teamId={teamId} section={section} teamName={teamName} />
    </SectionPage>
  )
}

function SectionPage({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string
  title: string
  children: React.ReactNode
}) {
  const location = useLocation()
  return (
    <div>
      <header className="mb-7">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
          {eyebrow}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {title}
        </h2>
      </header>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

function Panel({
  kind,
  teamId,
  section,
  teamName,
}: {
  kind: TeamKind
  teamId: string
  section: string
  teamName: string
}) {
  if (kind === "group") {
    if (section === "business-info") return <GroupSettings groupId={teamId} />
    if (section === "members")
      return <TeamMembersPanel teamId={teamId} teamName={teamName} teamKind="group" />
    if (section === "network") return <OrgChartPanel />
    return <NotFound />
  }

  if (kind === "dealership") {
    if (section === "business-info")
      return <StoreSettings teamId={teamId} teamKind="dealership" />
    if (section === "members")
      return <TeamMembersPanel teamId={teamId} teamName={teamName} teamKind="dealership" />
    if (section === "service-lane") return <ServiceLaneSettings teamId={teamId} />
    if (section === "goals") return <GoalsRewardsSettings teamId={teamId} />
    if (section === "body-shop")
      return <BodyShopsSettings dealershipId={teamId} dealershipName={teamName} />
    return <NotFound />
  }

  if (kind === "bodyshop") {
    if (section === "business-info")
      return <StoreSettings teamId={teamId} teamKind="bodyshop" />
    if (section === "members")
      return <TeamMembersPanel teamId={teamId} teamName={teamName} teamKind="bodyshop" />
    if (section === "fees") return <FeesSettings teamId={teamId} teamKind="bodyshop" />
    if (section === "intake") return <ConsumerIntakeSettings teamId={teamId} />
    if (section === "dealers")
      return <DealershipsSettings bodyshopId={teamId} bodyshopName={teamName} />
    if (section === "marketing")
      return <MarketingSuperchargeSettings teamId={teamId} />
    if (section === "booking") return <BookingCalendarSettings teamId={teamId} />
    return <NotFound />
  }

  return <NotFound />
}

function NotFound() {
  return <p className="text-sm text-zinc-500">Section not found.</p>
}

function lastVisitedTarget(
  last: LastVisited | null,
  ctx: { groupId: string | null; dealershipId: string | null; bodyshopId: string | null },
): string | null {
  if (!last) return null
  if (last.kind === "account") return "/settings/account"
  if (!last.teamId || !last.section) return null
  // The recorded team must still belong to the user (org context lists
  // only teams the user is a member of). If they've been removed from
  // that team since, fall back to the chain.
  if (last.kind === "group" && ctx.groupId === last.teamId)
    return `/settings/group/${last.teamId}/${last.section}`
  if (last.kind === "dealership" && ctx.dealershipId === last.teamId)
    return `/settings/dealership/${last.teamId}/${last.section}`
  // For bodyshop we accept the saved teamId even if it doesn't match
  // activeBodyShopId — the user might have multiple body shops; the
  // sidebar's entity selector lists them all and the URL→context sync
  // will flip the active one on render.
  if (last.kind === "bodyshop") return `/settings/bodyshop/${last.teamId}/${last.section}`
  return null
}

function legacyTabRedirect(
  tab: string,
  ctx: { groupId: string | null; dealershipId: string | null; bodyshopId: string | null },
): string | null {
  switch (tab) {
    case "account":
      return "/settings/account"
    case "group":
      return ctx.groupId
        ? `/settings/group/${ctx.groupId}/business-info`
        : "/settings/account"
    case "store":
    case "business-info":
      if (ctx.dealershipId) return `/settings/dealership/${ctx.dealershipId}/business-info`
      if (ctx.bodyshopId) return `/settings/bodyshop/${ctx.bodyshopId}/business-info`
      if (ctx.groupId) return `/settings/group/${ctx.groupId}/business-info`
      return "/settings/account"
    case "team":
      if (ctx.dealershipId) return `/settings/dealership/${ctx.dealershipId}/members`
      if (ctx.bodyshopId) return `/settings/bodyshop/${ctx.bodyshopId}/members`
      if (ctx.groupId) return `/settings/group/${ctx.groupId}/members`
      return "/settings/account"
    case "fees":
      return ctx.bodyshopId
        ? `/settings/bodyshop/${ctx.bodyshopId}/fees`
        : "/settings/account"
    case "bodyshops":
      return ctx.dealershipId
        ? `/settings/dealership/${ctx.dealershipId}/body-shop`
        : "/settings/account"
    case "dealerships":
      if (ctx.bodyshopId) return `/settings/bodyshop/${ctx.bodyshopId}/dealers`
      if (ctx.groupId) return `/settings/group/${ctx.groupId}/network`
      return "/settings/account"
    case "goals":
      return ctx.dealershipId
        ? `/settings/dealership/${ctx.dealershipId}/goals`
        : "/settings/account"
    default:
      return null
  }
}
